/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	ViewEncapsulation,
} from "@angular/core";
import {
	darkenRGBColor,
	lightenRGBColor,
	RGB_COLORS,
} from "@app/constants/colors.const";
import { IconComponent } from "../icon/icon.component";

@Component({
	selector: "cb-avatar",
	standalone: true,
	imports: [CommonModule, IconComponent],
	templateUrl: "./avatar.component.html",
	styleUrl: "./avatar.component.scss",
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarComponent {
	public readonly size = input<"xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl">("md");

	public readonly type = input<
		"image" | "icon" | "placeholder" | "initials" | "textOnly"
	>("placeholder");

	public readonly profileIcon = input<string>();

	public readonly iconType = input<"none" | "dot" | "verified" | "custom">("none");

	public readonly icon = input<string>();

	public readonly url = input<string>();

	public readonly customInitials = input<string | null>();

	public readonly label = input.required<string | null>();
	public readonly subtitle = input<string | null>(null);

	public readonly onlyAvatar = input<boolean>(false);
	public readonly randomColor = input<boolean>(false);

	/**
	 * The label to use for the initials when
	 * the user wants to override the default initials from "label"
	 */
	public readonly initialsLabel = input<string>();

	public readonly generatedRandomColor = computed(() => {
		const label = this.label();

		if (!label || label.length === 0) return "";

		const hash = label
			.trim()
			.split("")
			.reduce((acc, char) => acc + char.charCodeAt(0), 0);
		const color = RGB_COLORS[hash % RGB_COLORS.length];

		return color;
	});

	public readonly generatedRandomIconColor = computed(() => {
		return lightenRGBColor(this.generatedRandomColor(), 40);
	});

	public readonly generatedRandomTextColor = computed(() => {
		return lightenRGBColor(this.generatedRandomColor(), 70);
	});

	public readonly generatedRandomBackground = computed(() => {
		return darkenRGBColor(this.generatedRandomColor(), 5);
	});

	public readonly initials = computed(() => {
		if (this.customInitials() && this.customInitials()!.length > 0) return this.customInitials();

		if (!this.label && !this.initialsLabel) {
			return "";
		}

		const label = this.initialsLabel() || this.label() || "";

		// Split the label into words using space as delimiter
		let words = label.split(" ");

		// Helper function to detect if a character is an emoji
		const isEmoji = (char: string): boolean => {
			// Comprehensive emoji regex using Unicode property escapes
			const emojiRegex = /\p{Emoji}/u;
			return emojiRegex.test(char);
		};

		// Filter out words that begin with an emoji
		words = words.filter((word) => {
			if (word.length === 0) return false;
			const firstChar = Array.from(word)[0]; // Correctly handles surrogate pairs
			return !isEmoji(firstChar);
		});

		// If no words left after filtering, return an empty string
		if (words.length === 0) {
			return "";
		}

		// Function to safely get the first N characters of a word
		const getFirstNChars = (word: string, n: number): string => {
			return Array.from(word).slice(0, n).join("");
		};

		// If there's only one word, get the first two characters
		if (words.length === 1) {
			const twoLetters = getFirstNChars(words[0], 2);
			return twoLetters.toUpperCase();
		}

		// If there are multiple words, get the first character from the first two words
		const firstInitial = getFirstNChars(words[0], 1);
		const secondInitial = getFirstNChars(words[1], 1);

		return (firstInitial + secondInitial).toUpperCase();
	});
}
